import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { useEffect } from 'react';
import { DictionaryEntry } from '../types';
import { DefinitionFooter } from './DefinitionFooter';

interface ModalCyberpunkProps {
    entry: DictionaryEntry | null;
    onClose: () => void;
    themeIndex?: number;
}

const getTheme = (index: number = 0) => {
    const themes = ['cyan', 'fuchsia', 'violet'] as const;
    return themes[index % themes.length];
};

const getThemeColors = (theme: 'cyan' | 'fuchsia' | 'violet'): {
    text: string;
    border: string;
    bg: string;
    cardBg: string;
    cardBorder: string;
    tagBg: string;
    tagBorder: string;
} => {
    switch (theme) {
        case 'cyan':
            return {
                text: 'text-cyan-400',
                border: 'border-cyan-500/30',
                bg: 'bg-cyan-500/10',
                cardBg: 'from-slate-900 via-slate-900 to-cyan-950/40',
                cardBorder: 'border-cyan-500/20',
                tagBg: 'bg-cyan-500/20',
                tagBorder: 'border-cyan-500/30'
            };
        case 'fuchsia':
            return {
                text: 'text-fuchsia-400',
                border: 'border-fuchsia-500/30',
                bg: 'bg-fuchsia-500/10',
                cardBg: 'from-slate-900 via-slate-900 to-fuchsia-950/40',
                cardBorder: 'border-fuchsia-500/20',
                tagBg: 'bg-fuchsia-500/20',
                tagBorder: 'border-fuchsia-500/30'
            };
        case 'violet':
            return {
                text: 'text-violet-400',
                border: 'border-violet-500/30',
                bg: 'bg-violet-500/10',
                cardBg: 'from-slate-900 via-slate-900 to-violet-950/40',
                cardBorder: 'border-violet-500/20',
                tagBg: 'bg-violet-500/20',
                tagBorder: 'border-violet-500/30'
            };
    }
};

export const ModalCyberpunk = ({ entry, onClose, themeIndex = 0 }: ModalCyberpunkProps) => {
    // Fermer avec Escape
    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };

        if (entry) {
            document.addEventListener('keydown', handleEscape);
            document.body.style.overflow = 'hidden';
        }

        return () => {
            document.removeEventListener('keydown', handleEscape);
            document.body.style.overflow = 'auto';
        };
    }, [entry, onClose]);

    if (!entry) return null;

    const theme = getTheme(themeIndex);
    const themeColors = getThemeColors(theme);

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={onClose}
                className="fixed inset-0 bg-black/80 backdrop-blur-md z-50 overflow-y-auto"
            >
                <div className="flex min-h-screen items-center justify-center p-4">
                    <motion.div
                        initial={{ scale: 0.9, opacity: 0, y: 20 }}
                        animate={{ scale: 1, opacity: 1, y: 0 }}
                        exit={{ scale: 0.9, opacity: 0, y: 20 }}
                        onClick={(e) => e.stopPropagation()}
                        className={`relative bg-gradient-to-br ${themeColors.cardBg} border ${themeColors.cardBorder} rounded-2xl p-10 max-w-4xl w-full shadow-2xl my-8 overflow-hidden`}
                    >
                        {/* Halo lumineux radial en haut à droite */}
                        <div
                            className="absolute top-0 right-0 w-64 h-64 opacity-30 pointer-events-none"
                            style={{
                                background: `radial-gradient(circle at top right, ${theme === 'cyan' ? 'rgba(34, 211, 238, 0.4)' :
                                    theme === 'fuchsia' ? 'rgba(217, 70, 239, 0.4)' :
                                        'rgba(139, 92, 246, 0.4)'
                                    }, transparent 70%)`
                            }}
                        ></div>

                        {/* Contenu */}
                        <div className="relative z-10">
                            {/* Header */}
                            <div className="mb-6">
                                <h2 className="text-5xl md:text-6xl font-sans font-bold text-white tracking-tight leading-none">
                                    {entry.term}
                                </h2>
                            </div>

                            {/* Définition courte (Lead) */}
                            <div className="mb-8">
                                <p className={`text-xl md:text-2xl leading-snug`}>
                                    <span className={`font-mono font-bold mr-3 ${themeColors.text}`}>{(entry.type || '').toLowerCase()}</span>
                                    <span className="text-slate-300 font-light">
                                        {entry.definition.split(' ').slice(0, -1).join(' ')} <span className="whitespace-nowrap">{entry.definition.split(' ').slice(-1)}</span>
                                    </span>
                                </p>
                            </div>

                            {/* Image d'illustration */}
                            {entry.image && (
                                <div className="mb-8 rounded-2xl overflow-hidden shadow-lg max-w-2xl mx-auto border border-slate-800/50">
                                    <div className="aspect-square w-full">
                                        <img
                                            src={entry.image}
                                            alt={entry.term}
                                            className="w-full h-full object-cover"
                                        />
                                    </div>
                                </div>
                            )}

                            {/* Définition complète */}
                            <div className="prose prose-invert prose-lg md:prose-xl max-w-none mb-10">
                                {entry.fullDefinition.split(/\n|\\n/).map((paragraph, index) => (
                                    <p key={index} className="text-slate-300 leading-relaxed mb-6 last:mb-0 font-light">
                                        {paragraph.trim()}
                                    </p>
                                ))}
                            </div>

                            {/* Tags */}
                            <div className="flex flex-wrap gap-3 pt-6 border-t border-slate-800/50">
                                {entry.tags.map((tag) => (
                                    <span
                                        key={tag}
                                        className={`px-4 py-2 ${themeColors.tagBg} ${themeColors.text} text-sm font-mono font-bold rounded-lg border ${themeColors.tagBorder} transition-colors`}
                                    >
                                        {tag}
                                    </span>
                                ))}
                            </div>
                            {/* Footer */}
                            <DefinitionFooter
                                entry={entry}
                                variant="cyberpunk"
                                accentColor={themeColors.text}
                            />
                        </div>

                        {/* Bouton fermer */}
                        <button
                            onClick={onClose}
                            className={`absolute top-6 right-6 p-2 text-slate-500 hover:${themeColors.text} hover:${themeColors.bg} rounded-full transition-all duration-300 z-50 group/close`}
                            aria-label="Fermer"
                        >
                            <X size={28} className="group-hover/close:rotate-90 transition-transform duration-300" />
                        </button>
                    </motion.div>
                </div>
            </motion.div>
        </AnimatePresence>
    );
};
